/**
 * EcosystemManager: the ecosystem-management section of the platform admin
 * area (P7 wave 3a).
 *
 * Renders the table of ALL tenant ecosystems (from `GET /api/ecosystems/
 * admin/all`, which excludes the platform pseudo-Ecosystem) and owns the
 * create / edit / archive flows:
 *   - Create: opens `EcosystemForm` in `create` mode in a modal.
 *   - Edit: opens `EcosystemForm` in `edit` mode, pre-filled.
 *   - Archive: an in-app confirm modal (NOT `window.confirm`), then a
 *     `POST .../archive` soft state change.
 *
 * States handled: loading spinner, load-error alert with retry, empty state,
 * and a per-action success / error banner.
 *
 * Server errors from create / edit (notably a slug-validation or duplicate
 * 400) are threaded back into `EcosystemForm` via its `serverError` prop so
 * they render inline next to the offending field area.
 *
 * No inline CSS: Bootstrap utilities / PlatformAdmin.css only.
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    Button,
    Badge,
    Spinner,
    Alert,
    Modal,
    Card,
} from 'react-bootstrap';

import { useEcosystemApi } from '@/shared/services/ecosystemApi';

import EcosystemForm from './EcosystemForm';
import EcosystemMembersModal from './EcosystemMembersModal';
import './PlatformAdmin.css';

// Bootstrap badge variant per lifecycle status.
const STATUS_VARIANT = {
    active: 'success',
    pending: 'warning',
    archived: 'secondary',
};

/**
 * A small status badge for the table.
 *
 * @param {{status: string}} props
 */
function StatusBadge({ status }) {
    const variant = STATUS_VARIANT[status] || 'light';
    return (
        <Badge bg={variant} text={variant === 'warning' ? 'dark' : undefined}>
            {status}
        </Badge>
    );
}

StatusBadge.propTypes = {
    // The ecosystem lifecycle status ('active' | 'pending' | 'archived').
    status: PropTypes.string.isRequired,
};

export default function EcosystemManager() {
    const ecosystemApi = useEcosystemApi();

    const [ecosystems, setEcosystems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    // Per-action banner: { variant: 'success' | 'danger', text }.
    const [banner, setBanner] = useState(null);

    // Create / edit modal. `editTarget` null => create mode; an ecosystem
    // object => edit mode for that record.
    const [showForm, setShowForm] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    // Archive confirmation. Holds the ecosystem pending archive, or null.
    const [archiveTarget, setArchiveTarget] = useState(null);
    const [archiving, setArchiving] = useState(false);

    // Members modal target: the ecosystem whose members are being viewed /
    // managed, or null when the modal is closed. P7 wave 3b.
    const [membersTarget, setMembersTarget] = useState(null);

    const loadEcosystems = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const data = await ecosystemApi.fetchAllEcosystems();
            setEcosystems(Array.isArray(data) ? data : []);
        } catch {
            setLoadError('Failed to load ecosystems. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [ecosystemApi]);

    useEffect(() => {
        loadEcosystems();
    }, [loadEcosystems]);

    // --- Create / edit -----------------------------------------------------

    const openCreate = () => {
        setEditTarget(null);
        setFormError('');
        setShowForm(true);
    };

    const openEdit = (ecosystem) => {
        setEditTarget(ecosystem);
        setFormError('');
        setShowForm(true);
    };

    const closeForm = () => {
        if (submitting) {
            return;
        }
        setShowForm(false);
        setEditTarget(null);
        setFormError('');
    };

    const handleFormSubmit = async (payload) => {
        setSubmitting(true);
        setFormError('');
        try {
            if (editTarget) {
                await ecosystemApi.updateEcosystem(editTarget.slug, payload);
                setBanner({
                    variant: 'success',
                    text: `Ecosystem "${payload.name}" updated.`,
                });
            } else {
                await ecosystemApi.createEcosystem(payload);
                setBanner({
                    variant: 'success',
                    text: `Ecosystem "${payload.name}" created.`,
                });
            }
            setShowForm(false);
            setEditTarget(null);
            await loadEcosystems();
        } catch (err) {
            // Surface the server's message inline in the form: a slug
            // format / reserved / duplicate 400 carries a clean message.
            const message =
                err?.response?.data?.message ||
                'Failed to save the ecosystem. Please try again.';
            setFormError(message);
        } finally {
            setSubmitting(false);
        }
    };

    // --- Archive -----------------------------------------------------------

    const handleArchiveConfirm = async () => {
        if (!archiveTarget) {
            return;
        }
        setArchiving(true);
        try {
            await ecosystemApi.archiveEcosystem(archiveTarget.slug);
            setBanner({
                variant: 'success',
                text: `Ecosystem "${archiveTarget.name}" archived.`,
            });
            setArchiveTarget(null);
            await loadEcosystems();
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                'Failed to archive the ecosystem. Please try again.';
            setBanner({ variant: 'danger', text: message });
            setArchiveTarget(null);
        } finally {
            setArchiving(false);
        }
    };

    // --- Render ------------------------------------------------------------

    return (
        <section>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                    <h2 className="h4 mb-1">Ecosystems</h2>
                    <p className="text-muted mb-0">
                        Create, edit, and archive the ecosystems hosted on the
                        platform.
                    </p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    + New Ecosystem
                </Button>
            </div>

            {banner && (
                <Alert
                    variant={banner.variant}
                    dismissible
                    onClose={() => setBanner(null)}
                >
                    {banner.text}
                </Alert>
            )}

            {loadError && (
                <Alert variant="danger">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                        <span>{loadError}</span>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={loadEcosystems}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            )}

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" role="status">
                        <span className="visually-hidden">Loading…</span>
                    </Spinner>
                </div>
            ) : !loadError && ecosystems.length === 0 ? (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="text-center py-5">
                        <p className="text-muted mb-3">
                            No ecosystems yet. Create the first hosted ecosystem
                            to get started.
                        </p>
                        <Button variant="primary" onClick={openCreate}>
                            + New Ecosystem
                        </Button>
                    </Card.Body>
                </Card>
            ) : !loadError ? (
                <Card className="border-0 shadow-sm">
                    <Table responsive hover className="mb-0 align-middle">
                        <thead>
                            <tr>
                                <th>Ecosystem</th>
                                <th>Slug</th>
                                <th>Status</th>
                                <th className="text-end">Members</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ecosystems.map((eco) => (
                                <tr key={eco._id || eco.slug}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            {eco.branding?.logoUrl ? (
                                                <img
                                                    src={eco.branding.logoUrl}
                                                    alt=""
                                                    className="platform-admin-logo"
                                                />
                                            ) : null}
                                            <div>
                                                <div className="fw-semibold">
                                                    {eco.name}
                                                </div>
                                                {eco.shortName && (
                                                    <div className="small text-muted">
                                                        {eco.shortName}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <code>{eco.slug}</code>
                                    </td>
                                    <td>
                                        <StatusBadge status={eco.status} />
                                    </td>
                                    <td className="text-end">
                                        {eco.memberCount ?? 0}
                                    </td>
                                    <td className="text-end">
                                        <div className="d-inline-flex gap-2">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() =>
                                                    setMembersTarget(eco)
                                                }
                                            >
                                                Members
                                            </Button>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                onClick={() => openEdit(eco)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                disabled={
                                                    eco.status === 'archived'
                                                }
                                                onClick={() =>
                                                    setArchiveTarget(eco)
                                                }
                                            >
                                                {eco.status === 'archived'
                                                    ? 'Archived'
                                                    : 'Archive'}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Card>
            ) : null}

            {/* Create / edit modal. Keyed by the target slug (or 'create') so
                EcosystemForm fully remounts (and rebuilds its initial state
                when switching between records or modes). */}
            <Modal
                show={showForm}
                onHide={closeForm}
                size="lg"
                backdrop="static"
                scrollable
            >
                <Modal.Header closeButton={!submitting}>
                    <Modal.Title>
                        {editTarget
                            ? `Edit ${editTarget.name}`
                            : 'New Ecosystem'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {showForm && (
                        <EcosystemForm
                            key={editTarget ? editTarget.slug : 'create'}
                            mode={editTarget ? 'edit' : 'create'}
                            ecosystem={editTarget}
                            submitting={submitting}
                            serverError={formError}
                            onSubmit={handleFormSubmit}
                            onCancel={closeForm}
                        />
                    )}
                </Modal.Body>
            </Modal>

            {/* Member management modal (P7 wave 3b). Lists the ecosystem's
                memberships joined to User identity and lets a platform admin
                set per-member roles to 'admin' or 'member'. The modal owns
                its own data: no need to re-fetch the outer table when a
                role changes. */}
            <EcosystemMembersModal
                show={membersTarget !== null}
                ecosystem={membersTarget}
                onHide={() => setMembersTarget(null)}
            />

            {/* Archive confirmation: in-app, not window.confirm. */}
            <Modal
                show={archiveTarget !== null}
                onHide={() => !archiving && setArchiveTarget(null)}
            >
                <Modal.Header closeButton={!archiving}>
                    <Modal.Title>Archive ecosystem?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Archiving <strong>{archiveTarget?.name}</strong> hides
                        it from the public directory and from URL routing. This
                        is a soft change: no data is deleted, and the ecosystem
                        can be reactivated later by editing its status.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setArchiveTarget(null)}
                        disabled={archiving}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="danger"
                        onClick={handleArchiveConfirm}
                        disabled={archiving}
                    >
                        {archiving ? (
                            <>
                                <Spinner
                                    animation="border"
                                    size="sm"
                                    className="me-2"
                                />
                                Archiving…
                            </>
                        ) : (
                            'Archive'
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </section>
    );
}
